import { getItemFromStorage, addItemToStorage, getRefreshedToken, deleteItemFromStorage } from '../utils';
import { SPOTIFY_TOKEN, SPOTIFY_REFRESH_TOKEN, SPOTIFY_TOKEN_EXPIRES_AT } from '../constants/storage';

interface TokenInfo {
  access_token: string;
  refresh_token: string;
  expires_at: number; // Unix timestamp
  expires_in: number; // Seconds from issue
}

class TokenManager {
  private static instance: TokenManager;
  private refreshPromise: Promise<string> | null = null;
  private lastTokenInfo: TokenInfo | null = null;

  // Singleton pattern
  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Smart token getter - returns valid token or refreshes if needed
   * This replaces all direct token usage
   */
  async getValidToken(): Promise<string> {
    const currentToken = getItemFromStorage(SPOTIFY_TOKEN);

    if (!currentToken) {
      throw new Error('No token available');
    }

    // Check if token is close to expiry (refresh 5 minutes early)
    if (this.isTokenNearExpiry(currentToken)) {
      return this.refreshToken();
    }

    return currentToken;
  }

  /**
   * Centralized token refresh with duplicate prevention
   */
  private async refreshToken(): Promise<string> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh();

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      throw error;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<string> {
    const refreshToken = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await getRefreshedToken(refreshToken);
      const newToken = response?.data?.access_token;

      if (!newToken) {
        throw new Error('Failed to get new token');
      }

      // Update storage
      addItemToStorage(SPOTIFY_TOKEN, newToken);

      const storedRefresh =
        response?.data?.refresh_token || getItemFromStorage(SPOTIFY_REFRESH_TOKEN) || refreshToken;

      // Calculate and store expiry time if provided
      if (response?.data?.expires_in) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;

        // Store in memory
        this.lastTokenInfo = {
          access_token: newToken,
          refresh_token: storedRefresh,
          expires_at: expiresAt,
          expires_in: response.data.expires_in,
        };

        // Store in localStorage (survives page reload)
        addItemToStorage(SPOTIFY_TOKEN_EXPIRES_AT, expiresAt.toString());
      }

      return newToken;
    } catch (error) {
      // getRefreshedToken already clears session on SPOTIFY_REAUTH_REQUIRED
      this.clearTokenInfo();
      throw error;
    }
  }

  /**
   * Check if token is near expiry (within 5 minutes)
   */
  private isTokenNearExpiry(token: string): boolean {
    const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
    
    // Check 1: Use in-memory token info (fast path)
    if (this.lastTokenInfo && this.lastTokenInfo.access_token === token) {
      return this.lastTokenInfo.expires_at <= fiveMinutesFromNow;
    }

    // Check 2: Use persisted expiry from localStorage
    const expiresAtStr = getItemFromStorage(SPOTIFY_TOKEN_EXPIRES_AT);
    if (expiresAtStr) {
      const expiresAt = parseInt(expiresAtStr);
      if (!isNaN(expiresAt)) {
        // Reconstruct lastTokenInfo from localStorage
        const refreshToken = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);
        this.lastTokenInfo = {
          access_token: token,
          refresh_token: refreshToken || '',
          expires_at: expiresAt,
          expires_in: Math.floor((expiresAt - Date.now()) / 1000),
        };
        return expiresAt <= fiveMinutesFromNow;
      }
    }

    // Check 3: Try to decode JWT (keep for compatibility)
    try {
      const decoded = this.decodeJWT(token);
      if (decoded && decoded.exp) {
        return decoded.exp * 1000 <= fiveMinutesFromNow;
      }
    } catch (error) {
      // Not a JWT or couldn't decode
    }

    // If we can't determine expiry, assume expired (safe default)
    // This forces a refresh check rather than using potentially expired token
    console.warn('⚠️ Token expiry unknown, assuming expired for safety');
    return true;
  }

  /**
   * Simple JWT decoder (without verification)
   */
  private decodeJWT(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  /**
   * Validate token without making external API calls
   * For internal checks only
   */
  isTokenProbablyValid(token: string): boolean {
    if (!token) return false;

    // Basic format check
    if (typeof token !== 'string' || token.length < 10) {
      return false;
    }

    // If we have expiry info, check it
    if (this.lastTokenInfo && this.lastTokenInfo.access_token === token) {
      return this.lastTokenInfo.expires_at > Date.now();
    }

    // For JWT tokens, check expiry
    try {
      const decoded = this.decodeJWT(token);
      if (decoded && decoded.exp) {
        return decoded.exp * 1000 > Date.now();
      }
    } catch {
      // Not JWT, assume valid for now
    }

    return true; // Assume valid if we can't determine otherwise
  }

  /**
   * Clear stored token info (for logout scenarios)
   */
  clearTokenInfo(): void {
    this.lastTokenInfo = null;
    this.refreshPromise = null;
    // Clear persisted expiry
    deleteItemFromStorage(SPOTIFY_TOKEN_EXPIRES_AT);
  }

  /**
   * Force token refresh (useful for manual refresh scenarios)
   */
  async forceRefresh(): Promise<string> {
    return this.refreshToken();
  }

  /**
   * Get token info (for debugging/monitoring)
   */
  getTokenInfo(): TokenInfo | null {
    return this.lastTokenInfo;
  }
}

export default TokenManager;
